import { useCallback, useEffect, useRef, useState } from "react";

import { Agent, AgentsSortOptionEnum } from "@/api/generated";
import { PropallyApiFactory } from "@/module/CustomApiFactory";

// 相性順のエージェント一覧取得（GET /v1/agents?sort=compatibility）。
// 既定は相性面の掲載対象の上位3名。includeAllPublic で公開対象全員（相性面・詳細検索のどちらかで掲載中）を
// 母集団にし、perPage を省略すれば全ページ（診断後の選択）、指定すればその件数の1ページだけ（ホーム）を取得する。
export const COMPATIBILITY_AGENTS_PER_PAGE = 3;
const ALL_PUBLIC_AGENTS_PER_PAGE = 100;

interface Options {
  includeAllPublic?: boolean;
  // 1ページの件数。指定時はそのページだけで打ち切る（上位 N 名だけ欲しい画面向け）
  perPage?: number;
}

interface UseCompatibilityAgents {
  agents: Agent[];
  isLoading: boolean;
  error: boolean;
  fetchAgents: () => Promise<void>;
}

// enabled が false の間はリクエストを送らない（取得開始の条件を呼び出し側が持つ画面向け）。
export const useCompatibilityAgents = (
  enabled = true,
  { includeAllPublic = false, perPage }: Options = {}
): UseCompatibilityAgents => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [isLoading, setIsLoading] = useState(enabled);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(false);
  // ホーム等、リクエスト中にユーザーが離脱しうる画面からも呼ばれるため、
  // アンマウント後に応答が届いても setState しないようガードする
  const mountedRef = useRef(true);

  const fetchAgents = useCallback(async () => {
    setIsLoading(true);
    setError(false);
    try {
      const api = PropallyApiFactory();
      const pageSize = perPage ?? (includeAllPublic ? ALL_PUBLIC_AGENTS_PER_PAGE : COMPATIBILITY_AGENTS_PER_PAGE);
      // 公開対象全員を丸ごと使う画面だけ全ページを辿る
      const followsAllPages = includeAllPublic && perPage === undefined;
      const allAgents: Agent[] = [];
      let page = 1;
      let totalPages = 1;
      do {
        const response = await api.v1AgentsGet(page, pageSize, AgentsSortOptionEnum.Compatibility, includeAllPublic, {
          withCredentials: true,
        });
        if (!mountedRef.current) return;
        allAgents.push(...(response.data?.agents ?? []));
        totalPages = followsAllPages ? response.data?.total_pages ?? 1 : 1;
        page += 1;
      } while (page <= totalPages);
      setAgents(allAgents);
    } catch (e) {
      console.error(e);
      if (mountedRef.current) setError(true);
    } finally {
      if (mountedRef.current) {
        setHasLoaded(true);
        setIsLoading(false);
      }
    }
  }, [includeAllPublic, perPage]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;
    fetchAgents();
  }, [enabled, fetchAgents]);

  // false→true の最初のレンダーでも取得前を loading とする（effect の開始を待たない）。
  return { agents, isLoading: isLoading || (enabled && !hasLoaded), error, fetchAgents };
};
